import classNames from 'classnames';
import * as React from 'react';
import { usePlannerContext } from '../../Planner/Context/PlannerContextProvider';
import { usePlannerTranslations } from '../../Planner/Context/PlannerTranslationsContextProvider';
import { Summary } from '../../Summary/Summary';
import { FormattedText } from '~/Features/Txt/TxtBase';
import type { WindowWidthDetectionOptions } from '~/Utils/Hooks/useWindowWidthDetection';
import { useWindowWidthDetection } from '~/Utils/Hooks/useWindowWidthDetection';
import { MediaBreakpoints } from '~/Utils/MediaBreakpoints';
import styles from './PlannerSummary.module.css';

interface ExpandablePlannerSummaryProps {
    isExpanded: boolean;
    handleOpen: () => void;
    handleClose: () => void;
}

interface AlwaysExpandedPlannerSummaryProps {
    isExpanded: true;
    handleOpen?: () => void;
    handleClose?: () => void;
}

interface BasePlannerSummaryProps {
    mediaDetectionOptions: WindowWidthDetectionOptions;
    mediaType: 'mobile' | 'desktop';
    onActionClick: () => void;
    className?: string;
}

type InternalPlannerSummaryProps = (ExpandablePlannerSummaryProps | AlwaysExpandedPlannerSummaryProps) &
    BasePlannerSummaryProps;

export type PlannerSummaryProps = Pick<InternalPlannerSummaryProps, 'onActionClick' | 'className'>;

export const MobilePlannerSummary: React.FC<PlannerSummaryProps> = props => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    return (
        <PlannerSummary
            {...props}
            isExpanded={isExpanded}
            handleOpen={() => setIsExpanded(true)}
            handleClose={() => setIsExpanded(false)}
            mediaType="mobile"
            mediaDetectionOptions={{
                condition: 'lessThan',
                breakpoint: MediaBreakpoints.Desktop,
            }}
        />
    );
};

export const DesktopPlannerSummary: React.FC<PlannerSummaryProps> = props => (
    <PlannerSummary
        {...props}
        isExpanded
        mediaType="desktop"
        mediaDetectionOptions={{
            condition: 'greaterOrEqual',
            breakpoint: MediaBreakpoints.Desktop,
        }}
    />
);

const PlannerSummary: React.FC<InternalPlannerSummaryProps> = props => {
    const { isExpanded, mediaDetectionOptions, mediaType, onActionClick, handleOpen, handleClose, className } = props;

    const { allSelectedSubjects, removeSelectedSubject, totalPoints, timeDetails } = usePlannerContext();
    const { creditsLabel, actions, description, summary } = usePlannerTranslations();
    const { tooManyCreditsFormat, creditsExceededWarningFormat } = description;
    const { expandContent } = actions;

    const recommendedPointsInSinglePurchase = timeDetails.fullTimeSemesterCredits;
    const hasSelectedSubjects = allSelectedSubjects.length !== 0;
    const overPoints = totalPoints > recommendedPointsInSinglePurchase;
    const isFooterActionDisabled = isExpanded && !hasSelectedSubjects;

    const matchesDevice = useWindowWidthDetection(mediaDetectionOptions);

    const handleCloseConditionally = () => handleClose && handleClose();
    const handleOpenConditionally = () => handleOpen && handleOpen();

    const handleFooterActionClick = () => {
        if (isExpanded) {
            onActionClick();
        }

        handleOpenConditionally();
    };

    if (matchesDevice === false) {
        return null;
    }

    return (
        <div
            className={classNames(
                styles.PlannerSummary,
                styles[`Variant--${mediaType}`],
                !hasSelectedSubjects && !isExpanded && styles['Variant--hidden'],
                className
            )}
        >
            <div className={styles.Inner}>
                <Summary
                    isExpanded={isExpanded}
                    handleClose={handleCloseConditionally}
                    onSubjectActionClick={removeSelectedSubject}
                    subjects={allSelectedSubjects}
                    heading={summary.heading}
                    footerAction={{
                        label: isExpanded ? summary.actionLabel : expandContent,
                        onClick: handleFooterActionClick,
                        isDisabled: isFooterActionDisabled,
                    }}
                    {...(overPoints && {
                        warning: {
                            heading: FormattedText(tooManyCreditsFormat, { creditsName: creditsLabel }),
                            description: FormattedText(creditsExceededWarningFormat, {
                                credits: recommendedPointsInSinglePurchase,
                                creditsName: creditsLabel,
                            }),
                        },
                    })}
                />
            </div>
        </div>
    );
};
